import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { capturePosthogException } from "@/lib/safe-posthog";

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data considered fresh for 30 seconds
        staleTime: 30 * 1000,
        // Keep cached data for 5 minutes
        gcTime: 5 * 60 * 1000,
        // Retry failed requests 3 times with exponential backoff
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus
        refetchOnWindowFocus: false,
        // Don't refetch on reconnect by default
        refetchOnReconnect: "always",
      },
      mutations: {
        // Retry mutations once
        retry: 1,
        retryDelay: 1000,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Log errors to PostHog for monitoring
        capturePosthogException(error, {
          extra: {
            queryKey: query.queryKey,
          },
        });
        console.error("Query error:", error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        // Log mutation errors to PostHog
        capturePosthogException(error, {
          extra: {
            mutationKey: mutation.options.mutationKey,
            variables,
          },
        });
        console.error("Mutation error:", error);
      },
    }),
  });
}

// Create a singleton instance for SSR
let browserQueryClient: QueryClient | undefined;

export function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always create a new QueryClient
    return createQueryClient();
  } else {
    // Browser: reuse the same QueryClient
    if (!browserQueryClient) browserQueryClient = createQueryClient();
    return browserQueryClient;
  }
}
